import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, EyeOff, CheckCircle, XCircle, Loader } from "lucide-react";

// "" (empty) is a valid, intentional value: the production build uses relative
// /api URLs behind Caddy (same origin). `||` would treat "" as unset and send
// the browser to localhost:8001; `??` only falls back when it is truly unset.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "http://localhost:8001";

export default function ClientResetPassword() {
  const [token, setToken] = useState("");
  const [step, setStep] = useState("validating"); // validating, set_password, success, error
  const [tokenData, setTokenData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract token from URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");

    if (!urlToken) {
      setStep("error");
      setError("No reset token provided");
      return;
    }

    setToken(urlToken);
    validateToken(urlToken);
  }, []);

  const validateToken = async (token) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/client/validate-token`, { token });
      if (res.data.token_type !== "password_reset") {
        setStep("error");
        setError("This link is not a valid password reset link");
        return;
      }
      setTokenData(res.data);
      setStep("set_password");
    } catch (err) {
      setStep("error");
      setError(err.response?.data?.detail || "Invalid or expired reset token");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/client/reset-password`, {
        token,
        password
      });

      setStep("success");

      // Redirect to login after 2 seconds - reset-password does not issue
      // an auth token itself, unlike activation, so the client logs in
      // fresh with their new password.
      setTimeout(() => {
        window.location.href = "/client";
      }, 2000);

    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = detail && typeof detail === "object"
        ? (detail.errors || []).join(", ") || detail.message
        : detail;
      setError(message || "Password reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "validating") {
    return (
      <div className="login-container" data-testid="client-reset-password-validating">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Password Reset</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <Loader size={48} className="spin" style={{margin: "0 auto", color: "var(--gold)"}}/>
            <p style={{marginTop: "1rem"}}>Validating your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="login-container" data-testid="client-reset-password-error">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Password Reset</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <XCircle size={64} style={{color: "var(--error)", margin: "0 auto"}}/>
            <h3 style={{marginTop: "1rem", color: "var(--error)"}}>Reset Link Invalid</h3>
            <p style={{marginTop: "0.5rem", color: "var(--text-secondary)"}}>{error}</p>
            <p style={{marginTop: "2rem", fontSize: "0.875rem"}}>
              <a href="/client">Return to login</a> and request a new reset link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="login-container" data-testid="client-reset-password-success">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Password Reset</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <CheckCircle size={64} style={{color: "var(--success)", margin: "0 auto"}}/>
            <h3 style={{marginTop: "1rem", color: "var(--success)"}}>Password Reset!</h3>
            <p style={{marginTop: "0.5rem", color: "var(--text-secondary)"}}>
              Your password has been reset successfully.
            </p>
            <p style={{marginTop: "1rem"}}>Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  // Set Password Form
  return (
    <div className="login-container" data-testid="client-reset-password-form">
      <div className="login-panel glass-panel">
        <div className="brand-lockup" style={{marginBottom: "2rem"}}>
          <h2>MedSpaBookings</h2>
          <span>Reset Your Password</span>
        </div>

        {tokenData && (
          <div style={{marginBottom: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "6px"}}>
            <p style={{margin: 0, fontWeight: 600}}>{tokenData.clinic_name}</p>
            <p style={{margin: "0.25rem 0 0 0", fontSize: "0.875rem", color: "var(--text-secondary)"}}>
              {tokenData.email}
            </p>
          </div>
        )}

        <form onSubmit={handleReset}>
          <label>
            New Password
            <div style={{position: "relative"}}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Minimum 8 characters"
                data-testid="client-reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-secondary)"
                }}
                data-testid="client-reset-password-toggle-password"
              >
                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </label>

          <label>
            Confirm New Password
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Re-enter your new password"
              data-testid="client-reset-password-confirm-input"
            />
          </label>

          {error && (
            <div style={{
              padding: "0.75rem",
              background: "var(--error-bg)",
              border: "1px solid var(--error)",
              borderRadius: "6px",
              color: "var(--error)",
              marginBottom: "1rem",
              fontSize: "0.875rem"
            }} data-testid="client-reset-password-error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="primary-btn"
            disabled={loading}
            data-testid="client-reset-password-submit"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div style={{marginTop: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "6px", fontSize: "0.875rem"}}>
          <p style={{margin: 0, fontWeight: 600, marginBottom: "0.5rem"}}>Password Requirements:</p>
          <ul style={{margin: 0, paddingLeft: "1.5rem"}}>
            <li>At least 8 characters</li>
            <li>At least one uppercase and one lowercase letter</li>
            <li>At least one number</li>
          </ul>
        </div>

        <p style={{marginTop: "1.5rem", fontSize: "0.875rem", color: "var(--text-secondary)", textAlign: "center"}}>
          Need help? Contact support@medspabookings.co
        </p>
      </div>
    </div>
  );
}
